/*
 * Copyright 2022, Corning Optical Communications
 * All Rights Reserved.
 */
import React from "react";
import { IGeoRegion } from "../../../../store/api/admin/types";
import { confirmDialogSelector } from "../../../../store/helpers/helperSelectors";
import { constStr } from "../../../../globals/constants";
import { DIALOGOPENFOR } from "../../../../globals/globals";
import { selectedGeoRegionSelector } from "../../../../store/filters/Nodes/selectors";
import { useSelector } from "react-redux";
import { InputField } from "../../../../components/Shared/Radix/Inputs/InputField";
import { Button } from "../../../../components/ui/button";
import ConfirmDialog from "../../../../components/Shared/Dialogs/ConfirmDialog";
import { Icon } from "../../../../components/Shared/Icon";

interface ISelectedRegion {
  selectedRegion: IGeoRegion;
  saveButtonEnabled: boolean;
  errMsg: { [key: string]: string };
  onNameChange: (event: any) => void;
  onNameBlur: (event: any) => void;
  deleteClickHandler: (event: any) => void;
  confirmDeleteHandler: () => void;
  submitHandler: (event: any) => void;
  geoRegions: IGeoRegion[];
}

const RegionInfo: React.FC<ISelectedRegion> = ({
  selectedRegion,
  saveButtonEnabled,
  errMsg,
  onNameChange,
  onNameBlur,
  deleteClickHandler,
  confirmDeleteHandler,
  submitHandler,
  geoRegions,
}) => {
  const selectedGeoRegion = useSelector(selectedGeoRegionSelector);
  const openConfirmDialog = useSelector(confirmDialogSelector);

  const tooltipMsg =
    selectedGeoRegion === selectedRegion?.name
      ? "Active Region can not be deleted."
      : (selectedRegion?.siteGroups?.length || 0) > 0
        ? "Region can not be deleted as there are associated sites in this region."
        : "";

  /* Adapter: InputField onChange(name, value) → parent onNameChange(event) */
  const handleNameChange = (_name: string, value: string) => {
    onNameChange({ target: { name: _name, value } } as any);
  };

  const handleRemoveAction = (actionName: string) => {
    if (actionName.toUpperCase() === constStr.yesDelete.toUpperCase()) {
      confirmDeleteHandler();
    }
  };

  return (
    <>
      {openConfirmDialog === DIALOGOPENFOR.REGION && (
        <ConfirmDialog actionName={constStr.yesDelete} handleClickAction={handleRemoveAction}>
          Are you sure you want to delete region <strong>{selectedRegion?.name || "Unknown"}</strong>?
        </ConfirmDialog>
      )}
      <div
        className="flex flex-col w-full overflow-y-auto pt-1 pr-3"
        style={{ color: "var(--foreground)" }}
      >
     
    
        <div className="mt-2"
          style={{
            border: "1px solid var(--color-border)",
            borderRadius: "var(--radius)",
            padding: "24px",
            boxShadow: "var(--shadow-card)",
            backgroundColor: "var(--background)",
          }}
        >
          <form autoComplete="off">
            <div className="space-y-5 max-w-lg">
              {tooltipMsg && (
                <p className="text-xs italic" style={{ color: "var(--warning)" }}>
                  {tooltipMsg}
                </p>
              )}
              <InputField
                required
                label="Name"
                name="name"
                value={selectedRegion.name}
                maxLength={50}
                placeholder="Enter region name"
                onChange={handleNameChange}
                onBlur={onNameBlur}
                tooltipText="Maximum length: 50 characters"
              />
            </div>
          </form>
          <div className="flex items-center gap-2 mt-4 pt-4">
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="gap-1.5 !text-destructive !border-destructive hover:bg-transparent hover:!text-destructive hover:!border-destructive"
              onClick={deleteClickHandler}
            >
              <Icon name="delete" size={18} />
              Delete Region
            </Button>

            <Button disabled={!saveButtonEnabled} onClick={submitHandler} className="bg-primary text-primary-foreground hover:bg-primary/90" variant="outline" size="sm">
              Save
            </Button>
          </div>
        </div>
      </div>
    </>
  );
};

export default RegionInfo;
